import React, { useCallback, useState } from 'react';
import AuthFloatingInput from '../../../components/auth/AuthFloatingInput';
import GoogleSignInButton from '../../../components/auth/GoogleSignInButton';
import { PASSWORD_HELP, computePasswordStrength } from '../../../utils/passwordStrength';
import type {
  GoogleProfilePrefill,
  SignupFieldChangeHandler,
  SignupWizardData,
  SignupWizardErrors,
} from './types';

/**
 * Base64url-decode a JWT payload.
 *
 * DISPLAY ONLY. This is never a trust decision: the raw credential is posted
 * to `/auth/google/signup`, which verifies the signature and derives the real
 * identity itself. We decode purely so step 1 can show the user which account
 * they picked instead of an opaque token. Deliberately hand-rolled — a JWT
 * library for three optional string claims is not worth a dependency.
 */
const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    // atob yields bytes, not characters: re-assemble UTF-8 so accented names survive.
    const utf8 = decodeURIComponent(
      Array.from(atob(padded))
        .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join('')
    );
    const parsed: unknown = JSON.parse(utf8);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
};

const claimString = (value: unknown): string => (typeof value === 'string' ? value : '');

/** Pull first/last/email out of a Google id_token payload, tolerating absences. */
const prefillFromCredential = (credential: string): GoogleProfilePrefill => {
  const claims = decodeJwtPayload(credential) ?? {};
  const fullNameParts = claimString(claims.name).split(' ').filter(Boolean);
  return {
    email: claimString(claims.email),
    firstName: claimString(claims.given_name) || fullNameParts[0] || '',
    lastName: claimString(claims.family_name) || fullNameParts.slice(1).join(' '),
  };
};

interface StepAccountProps {
  data: SignupWizardData;
  errors: SignupWizardErrors;
  disabled: boolean;
  /**
   * A Google credential has been captured: the password field is gone (the
   * id_token is the credential) and the prefilled identity is read-only,
   * because the server takes it from the verified token, not from this form.
   */
  googleLinked: boolean;
  onChange: SignupFieldChangeHandler;
  onGoogleCredential: (credential: string, profile: GoogleProfilePrefill) => void;
  onUseEmailInstead: () => void;
}

/** Step 1 of the sign-up wizard: the person. Presentational — the host owns state. */
const StepAccount: React.FC<StepAccountProps> = ({
  data,
  errors,
  disabled,
  googleLinked,
  onChange,
  onGoogleCredential,
  onUseEmailInstead,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const strength = computePasswordStrength(data.password);

  const handleCredential = useCallback(
    (credential: string) => {
      onGoogleCredential(credential, prefillFromCredential(credential));
    },
    [onGoogleCredential]
  );

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3">
        <AuthFloatingInput
          label="First name"
          id="firstName"
          name="firstName"
          type="text"
          autoComplete="given-name"
          required
          readOnly={googleLinked}
          value={data.firstName}
          onChange={onChange}
          disabled={disabled}
          error={errors.firstName}
          aria-invalid={errors.firstName ? true : undefined}
        />
        <AuthFloatingInput
          label="Last name"
          id="lastName"
          name="lastName"
          type="text"
          autoComplete="family-name"
          required
          readOnly={googleLinked}
          value={data.lastName}
          onChange={onChange}
          disabled={disabled}
          error={errors.lastName}
          aria-invalid={errors.lastName ? true : undefined}
        />
      </div>

      <AuthFloatingInput
        label="Work email"
        id="email"
        name="email"
        type="email"
        autoComplete="email"
        required
        readOnly={googleLinked}
        value={data.email}
        onChange={onChange}
        disabled={disabled}
        error={errors.email}
        aria-invalid={errors.email ? true : undefined}
      />

      {/* No password on the Google path: the verified id_token IS the credential. */}
      {!googleLinked && (
        <div>
          <AuthFloatingInput
            label="Password"
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            required
            value={data.password}
            onChange={onChange}
            disabled={disabled}
            error={errors.password}
            aria-invalid={errors.password ? true : undefined}
            aria-describedby="password-hint"
            endAdornment={
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="p-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            }
          />
          <p id="password-hint" className="mt-1.5 text-xs text-text-secondary">
            {PASSWORD_HELP}
          </p>
          {data.password && (
            <div className="mt-2 flex items-center gap-2">
              <span className="flex h-1 flex-1 gap-1" aria-hidden="true">
                {[0, 1, 2, 3].map((segment) => (
                  <span
                    key={segment}
                    className={`h-1 flex-1 rounded-full ${
                      segment < strength.score ? strength.barClass : 'bg-border-primary/20'
                    }`}
                  />
                ))}
              </span>
              <span className="text-xs font-medium text-text-secondary">{strength.label}</span>
            </div>
          )}
        </div>
      )}

      {googleLinked ? (
        <div className="rounded-lg border border-border-primary/20 bg-white dark:bg-grey-950 p-3.5">
          <p className="text-sm text-text-primary">
            You&apos;re signing up with your Google account, so there is no password to choose.
          </p>
          <button
            type="button"
            onClick={onUseEmailInstead}
            disabled={disabled}
            className="mt-2 text-sm font-medium text-primary-500 hover:underline disabled:opacity-50"
          >
            Use email and password instead
          </button>
        </div>
      ) : (
        <div>
          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border-primary/20" />
            </div>
            <div className="relative flex justify-center">
              <span className="px-3 bg-white dark:bg-grey-950 text-xs text-text-secondary uppercase tracking-wider">
                or
              </span>
            </div>
          </div>

          <div className="flex justify-center">
            {/* onCredential keeps the credential in the wizard: signing up still
                needs the organization + legal answers from steps 2-3. */}
            <GoogleSignInButton mode="signup" disabled={disabled} onCredential={handleCredential} />
          </div>
        </div>
      )}
    </div>
  );
};

export default StepAccount;
